import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { trucksAPI, companiesAPI, printingAPI } from '../lib/api';
import { PrintMenu } from '../components/business/PrintMenu';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import {
  Plus,
  Loader2,
  Search,
  Pencil,
  Trash2,
  BarChart3,
  Radio,
  LayoutGrid,
  List,
  Columns3,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { cn, formatVolume } from '../lib/utils';
import { effectiveTruckStatus } from '../lib/truckStatus';
import { libelle, nomLisible } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { toast } from '../components/ui/sonner';
import { PageHeader } from '../components/ui/page-header';
import { ViewSwitcher } from '../components/ui/view-switcher';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { Citerne } from '../components/ui/icons/domain-icons';
import { LEXIQUE } from '../lib/lexique';
import TruckFormDialog from '../components/truck/TruckFormDialog';
import BoutonImportExcel from '../components/fleetImport/BoutonImportExcel';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

// Plaque, citerne ou châssis: on compare sans espaces, tirets ni casse, comme
// le serveur (`GET /trucks?q=`).
// Propriété du parc: valeur de l'adresse -> valeur de l'API.
const PROPRIETES = { propres: 'PROPRE', loues: 'LOUE' };
const OPTIONS_PROPRIETE = [
  { value: '', label: 'Tous' },
  { value: 'propres', label: 'Propres' },
  { value: 'loues', label: 'Loués' },
];
const isRented = (truck) => truck?.ownership_type === 'LOUE';

// Dispositions de la liste: valeur de l'adresse (`?vue=`) -> options du sélecteur.
const VUES = ['cartes', 'tableau', 'statut'];
const CLE_VUE = 'ecarbu.camions.vue';
const OPTIONS_VUE = [
  { value: 'cartes', titre: 'Cartes', texteLecteur: 'Afficher en cartes', icone: LayoutGrid, testid: 'trucks-view-cartes' },
  { value: 'tableau', titre: 'Tableau', texteLecteur: 'Afficher en tableau', icone: List, testid: 'trucks-view-tableau' },
  { value: 'statut', titre: 'Par statut', texteLecteur: 'Afficher en colonnes par statut', icone: Columns3, testid: 'trucks-view-statut' },
];
const lireVueMemorisee = () => {
  try {
    const valeur = window.localStorage.getItem(CLE_VUE);
    return VUES.includes(valeur) ? valeur : null;
  } catch (err) {
    return null;
  }
};
const memoriserVue = (vue) => {
  try {
    window.localStorage.setItem(CLE_VUE, vue);
  } catch (err) {
    // stockage indisponible (navigation privée…): le choix reste dans l'adresse
  }
};

// Colonnes de la vue « Par statut »: les états du parc regroupés comme l'exploitant les lit.
const COLONNES_STATUT = [
  { id: 'en-trajet', titre: 'En trajet', statuts: ['ON_TRIP'] },
  { id: 'disponibles', titre: 'Disponibles', statuts: ['FREE'] },
  { id: 'atelier', titre: 'À l’atelier', statuts: ['MAINTENANCE', 'IMMOBILIZED', 'OUT_OF_SERVICE'] },
  { id: 'loues', titre: 'Loués à un tiers', statuts: ['RENTED_OUT'] },
  { id: 'rendus', titre: 'Rendus', statuts: ['RETURNED_TO_OWNER'] },
];

// Colonnes du tableau (sans `cle`: non triable).
const COLONNES_TABLEAU = [
  { cle: 'tracteur', titre: 'Plaque tracteur' },
  { cle: 'citerne', titre: 'Plaque citerne' },
  { cle: 'propriete', titre: 'Propriété' },
  { cle: 'statut', titre: 'Statut' },
  { cle: 'chauffeur', titre: 'Chauffeur' },
  { cle: 'capacite', titre: 'Capacité', droite: true },
  { cle: 'technique', titre: 'État technique' },
  { titre: 'Suivi GPS' },
  { titre: 'Actions', droite: true },
];

// Colonnes triables du tableau.
const clePlaque = (t) => normalizeIdentifier(t.matricule);
const CLES_TRI = {
  tracteur: clePlaque,
  citerne: (t) => normalizeIdentifier(t.tank_number),
  propriete: (t) => (t.ownership_type === 'LOUE' ? 'Loué' : 'Propre'),
  statut: (t) => libelle('truckStatus', effectiveTruckStatus(t)),
  chauffeur: (t) => String(t.driver_full_name || '').toLowerCase(),
  capacite: (t) => Number(t.capacity_liters) || 0,
  technique: (t) => libelle('technicalStatus', t.technical_status, 'En bon état'),
};

const normalizeIdentifier = (value) =>
  String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

/**
 * Le parc. Chaque carte ouvre la fiche du camion (`/trucks/:id`): le tiroir de
 * détails et le dialogue « Profil camion » qui vivaient ici ont rejoint cette
 * fiche, avec leurs deux calculs de bénéfice faits dans le navigateur — des
 * chiffres que rien côté serveur ne confirmait.
 *
 * La recherche et la page courante vivent dans l'adresse: revenir de la fiche
 * ramène à la même page du parc, pas à la première.
 */
const TrucksPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trucks, setTrucks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [totalTrucks, setTotalTrucks] = useState(0);
  const [serverCounts, setServerCounts] = useState(null);
  // État du parc entier (en trajet, disponibles, immobilisés), côté serveur: la page reçue n'en montre qu'une tranche.
  const [serverStatuts, setServerStatuts] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [truckToEdit, setTruckToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [truckToDelete, setTruckToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tri, setTri] = useState({ cle: null, sens: 'asc' });
  const [vueMemorisee] = useState(lireVueMemorisee);

  const canCreate = true;
  const urlQuery = searchParams.get('q') || '';
  const rawPropriete = searchParams.get('propriete');
  const propriete = PROPRIETES[rawPropriete] ? rawPropriete : '';
  const rawVue = searchParams.get('vue');
  const vue = VUES.includes(rawVue) ? rawVue : vueMemorisee || 'cartes';

  const updateParams = useCallback(
    (changes) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(changes).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '' || value === 1) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const changerVue = useCallback(
    (valeur) => {
      memoriserVue(valeur);
      updateParams({ vue: valeur });
    },
    [updateParams],
  );

  // Une nouvelle recherche repart de la première page. Au montage, la
  // recherche relue dans l'adresse est déjà la bonne: la page est gardée.
  useEffect(() => {
    if (debouncedSearch === urlQuery) return;
    updateParams({ q: debouncedSearch.trim(), page: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Filtre immédiat pendant la frappe (et hors ligne, où le cache renvoie tout
  // le parc): sur les quatre numéros, comme la recherche serveur.
  const filteredTrucks = useMemo(() => {
    const q = normalizeIdentifier(searchQuery);
    // Hors ligne le cache renvoie tout le parc: la propriété se filtre ici aussi.
    const byOwnership = propriete
      ? trucks.filter((truck) => isRented(truck) === (propriete === 'loues'))
      : trucks;
    if (!q) return byOwnership;
    return byOwnership.filter((truck) =>
      [
        truck.matricule_normalized || truck.matricule,
        truck.tank_number,
        truck.tractor_vin,
        truck.tank_vin,
      ].some((value) => normalizeIdentifier(value).includes(q)),
    );
  }, [searchQuery, trucks, propriete]);

  // Pagination commune (barre en bas, `?page=` et `?taille=` dans l'adresse).
  // Le serveur renvoie le total; hors ligne, la liste filtrée sert de total et
  // la page se découpe ici. Une recherche ou un filtre ramène à la page 1.
  const pagination = usePaginationServeur(totalTrucks ?? filteredTrucks.length, {
    reinitialiser: `${urlQuery}|${propriete}`,
  });
  const { page, limit: pageSize } = pagination;

  const loadCompanies = useCallback(async () => {
    try {
      const result = await companiesAPI.getAll();
      const items = Array.isArray(result) ? result : result?.items || result?.data || [];
      setCompanies(items);
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  }, []);

  const nouvelleDemande = useDerniereDemande();
  const loadTrucks = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    if (!silencieux) setIsLoading(true);
    try {
      const result = await trucksAPI.getAllPaged({
        page,
        page_size: pageSize,
        q: urlQuery,
        ...(propriete ? { ownership_type: PROPRIETES[propriete] } : {}),
      });
      if (perimee()) return;
      setTrucks(result.items || []);
      setTotalTrucks(result.total ?? null);
      setServerCounts(result.counts || null);
      setServerStatuts(result.statuts || null);
    } catch (err) {
      if (perimee()) return;
      console.error('Failed to load trucks:', err);
    } finally {
      if (!perimee()) setIsLoading(false);
    }
  }, [page, pageSize, urlQuery, propriete, nouvelleDemande]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    loadTrucks();
  }, [loadTrucks]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['camions', 'maintenance', 'locations', 'repartition', 'documents', 'chauffeurs', 'trajets'], loadTrucks);


  const openTruck = useCallback(
    (truck, tab) => {
      navigate(`/trucks/${truck.id}${tab ? `?tab=${tab}` : ''}`, {
        state: { from: `${location.pathname}${location.search}` },
      });
    },
    [navigate, location.pathname, location.search],
  );

  // Lien profond `?matricule=` (recherche globale, anciens liens): résolu par
  // le serveur — pas seulement parmi les camions de la première page — puis
  // remplacé par la fiche du camion.
  const deepLinkMatricule = searchParams.get('matricule');
  const resolvingDeepLink = useRef(false);
  useEffect(() => {
    if (!deepLinkMatricule || resolvingDeepLink.current) return;
    resolvingDeepLink.current = true;
    const wanted = normalizeIdentifier(deepLinkMatricule);
    (async () => {
      try {
        const result = await trucksAPI.getAllPaged({ q: deepLinkMatricule, page: 1, page_size: 10 });
        const items = result?.items || [];
        const match =
          items.find(
            (truck) =>
              normalizeIdentifier(truck.matricule_normalized || truck.matricule) === wanted,
          ) || (items.length === 1 ? items[0] : null);
        if (match) {
          navigate(`/trucks/${match.id}`, { replace: true, state: { from: '/trucks' } });
          return;
        }
      } catch (err) {
        console.warn('Deep link resolution failed', err);
      }
      // Pas de camion unique: on montre la recherche correspondante.
      setSearchQuery(deepLinkMatricule);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('matricule');
          return next;
        },
        { replace: true },
      );
      resolvingDeepLink.current = false;
    })();
  }, [deepLinkMatricule, navigate, setSearchParams]);

  const openCreate = () => {
    setTruckToEdit(null);
    setFormOpen(true);
  };

  const openEdit = (truck) => {
    setTruckToEdit(truck);
    setFormOpen(true);
  };

  const confirmDelete = (truck) => {
    setTruckToDelete(truck);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!truckToDelete) return;
    setIsSubmitting(true);
    try {
      await trucksAPI.delete(truckToDelete.id);
      setShowDeleteModal(false);
      setTruckToDelete(null);
      toast.success('Camion supprimé');
      await loadTrucks();
    } catch (err) {
      console.error('Failed to delete truck:', err);
      toast.error(messageErreur(err, 'La suppression a échoué. Réessayez.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Le serveur découpe le parc en pages (`page` + `include_total`); seul le
  // cache hors ligne renvoie tout le parc, et la page se découpe alors ici.
  // Le tri par colonne ordonne la page affichée.
  const sortedTrucks = useMemo(() => {
    const lire = CLES_TRI[tri.cle];
    if (!lire) return filteredTrucks;
    const sens = tri.sens === 'desc' ? -1 : 1;
    return [...filteredTrucks].sort((a, b) => {
      const va = lire(a);
      const vb = lire(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sens;
      return String(va).localeCompare(String(vb), 'fr', { numeric: true }) * sens;
    });
  }, [filteredTrucks, tri]);

  const pagedTrucks = useMemo(() => {
    if (sortedTrucks.length <= pageSize) return sortedTrucks;
    const start = (page - 1) * pageSize;
    return sortedTrucks.slice(start, start + pageSize);
  }, [sortedTrucks, page, pageSize]);

  const trierPar = (cle) =>
    setTri((courant) =>
      courant.cle === cle
        ? { cle, sens: courant.sens === 'asc' ? 'desc' : 'asc' }
        : { cle, sens: 'asc' },
    );

  // Sans total du serveur (hors ligne), le total est celui de la liste filtrée.
  const shownTotal = totalTrucks ?? filteredTrucks.length;

  // Compteurs des pastilles: ceux du serveur (tout le parc, hors filtre de
  // propriété), sinon calculés sur la liste reçue quand elle n'est pas filtrée.
  const ownershipCounts = useMemo(() => {
    if (serverCounts) return serverCounts;
    if (propriete) return null;
    const loues = trucks.filter(isRented).length;
    return { propres: trucks.length - loues, loues };
  }, [serverCounts, propriete, trucks]);
  // Les quatre chiffres du coin d'en-tête: l'état du parc entier, recherche et
  // propriété comprises, tel que le serveur le compte. Sans lui (hors ligne),
  // ils se calculent sur la liste reçue, qui est alors le parc entier.
  const statsParc = useMemo(() => {
    const compte = (...codes) =>
      trucks.filter((truck) => codes.includes(effectiveTruckStatus(truck))).length;
    const enTrajet = serverStatuts?.enTrajet ?? compte('ON_TRIP');
    const disponibles = serverStatuts?.disponibles ?? compte('FREE');
    const immobilises = serverStatuts?.immobilises ?? compte('MAINTENANCE', 'IMMOBILIZED');
    return [
      { label: 'En trajet', valeur: enTrajet },
      { label: 'Disponibles', valeur: disponibles, ton: 'positif' },
      {
        label: 'Immobilisés',
        valeur: immobilises,
        ton: immobilises > 0 ? 'attention' : 'neutre',
      },
      {
        label: 'Loués',
        valeur: ownershipCounts?.loues ?? trucks.filter(isRented).length,
        onClick: () => updateParams({ propriete: 'loues', page: null }),
        aide: 'Afficher uniquement les camions loués',
      },
    ];
  }, [trucks, ownershipCounts, serverStatuts, updateParams]);

  const companiesById = useMemo(() => {
    const map = new Map();
    companies.forEach((company) => company?.id && map.set(company.id, company));
    return map;
  }, [companies]);

  const getTechnicalBadge = (status) => {
    const colors = {
      OK: 'bg-success/15 text-success border-success/40',
      MAINTENANCE: 'bg-flame/15 text-flame-ink border-flame/40',
      BROKEN: 'bg-destructive/15 text-destructive border-destructive/40',
    };
    return colors[status] || '';
  };

  const getGpsBadge = (truck) => {
    if (!truck?.gps_device_enabled) {
      return { label: 'Position par l’application du chauffeur', className: 'bg-muted text-muted-foreground border-border' };
    }
    if (truck.gps_status === 'live') {
      return { label: 'Boîtier GPS : position à jour', className: 'bg-success/15 text-success border-success/30' };
    }
    if (truck.gps_status === 'stale') {
      return { label: 'Boîtier GPS : position ancienne', className: 'bg-flame/15 text-flame-ink border-flame/30' };
    }
    if (truck.gps_status === 'conflict') {
      return { label: 'Boîtier GPS : positions en désaccord', className: 'bg-destructive/15 text-destructive border-destructive/30' };
    }
    return { label: 'Boîtier GPS configuré', className: 'bg-info/15 text-info border-info/30' };
  };

  const renderActions = (truck) => (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={(e) => {
          e.stopPropagation();
          openEdit(truck);
        }}
        aria-label="Modifier camion"
        data-testid={`edit-truck-${truck.id}`}
      >
        <Pencil className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={(e) => {
          e.stopPropagation();
          openTruck(truck, 'apercu');
        }}
        aria-label="Fiche camion"
        title="Fiche camion"
      >
        <BarChart3 className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          confirmDelete(truck);
        }}
        aria-label="Supprimer camion"
        data-testid={`delete-truck-${truck.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  // Le squelette ne remplace la page qu'au premier chargement: pendant une
  // recherche, il ferait disparaître le champ sous les doigts.
  if (isLoading && trucks.length === 0) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="trucks-page">
      <PageHeader
        eyebrow="Ressources"
        title="Camions"
        description={`${shownTotal} camion${shownTotal > 1 ? 's' : ''} ${propriete ? (propriete === 'loues' ? 'loués' : 'propres') : 'enregistrés'}`}
        stats={statsParc}
        statsDisposition="ligne"
        actions={
          <>
            {/* Imprimer le parc est une lecture: elle reste offerte à qui ne
                peut pas créer de camion. */}
            <PrintMenu
              label="Imprimer"
              items={[
                {
                  label: 'Parc par état',
                  filename: 'parc-par-etat',
                  url: printingAPI.trucksByStatus,
                  formats: ['pdf', 'xlsx', 'csv'],
                  // Le document reprend la recherche et la propriété de l'écran.
                  params: {
                    include_rented: true,
                    ...(urlQuery ? { q: urlQuery } : {}),
                    ...(propriete ? { ownership_type: PROPRIETES[propriete] } : {}),
                  },
                },
              ]}
            />
            <BoutonImportExcel />
            {canCreate && (
              <Button onClick={openCreate} data-testid="new-truck-btn">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nouveau camion
              </Button>
            )}
          </>
        }
      />

      {/* Barre d'outils: recherche, propriété, puis disposition à droite */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="trucks-toolbar">
        {/* Recherche bornée (≤ 18 rem dès 1024 px): filtres et disposition
            tiennent sur la même rangée en grand écran. */}
        <div className="relative w-full md:w-[40%] md:max-w-md lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
          <Input
            placeholder="Rechercher par plaque, citerne ou châssis…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-10"
            data-testid="trucks-search"
          />
        </div>

        {/* Propriété: Tous · Propres · Loués, avec le nombre de camions de chacun */}
        <div
          role="group"
          aria-label="Propriété du camion"
          className="flex flex-wrap items-center gap-2"
          data-testid="trucks-ownership-filter"
        >
          {OPTIONS_PROPRIETE.map((option) => {
            const active = propriete === option.value;
            const count =
              option.value === '' ? null : ownershipCounts?.[option.value];
            return (
              <Button
                key={option.value || 'tous'}
                type="button"
                size="sm"
                variant={active ? 'default' : 'outline'}
                aria-pressed={active}
                onClick={() => updateParams({ propriete: option.value, page: null })}
                data-testid={`trucks-ownership-${option.value || 'tous'}`}
              >
                {option.label}
                {count !== null && count !== undefined ? ` ${count}` : ''}
              </Button>
            );
          })}
        </div>

        <div
          className="flex flex-wrap items-center gap-2 md:ml-auto"
          data-testid="trucks-layout"
        >
          <ViewSwitcher
            value={vue}
            onChange={changerVue}
            options={OPTIONS_VUE}
            ariaLabel="Disposition de la liste"
            testid="trucks-view-switcher"
          />
        </div>
      </div>

      {/* Trucks Grid */}
      {pagedTrucks.length === 0 ? (
        /* `EmptyState` porte déjà son propre cadre: l'emboîter dans une carte
           en donnait deux. */
        <EmptyState
          variant={searchQuery || propriete ? 'filtered' : 'empty'}
          icon={Citerne}
          title={searchQuery || propriete ? 'Aucun résultat' : 'Aucun camion enregistré'}
          description={
            searchQuery || propriete
              ? 'Aucun camion ne correspond à cette recherche ou à ce filtre.'
              : 'La plaque et la capacité suffisent. Un camion enregistré peut ensuite être affecté à un trajet, suivi en carte et entretenu.'
          }
          actionLabel={searchQuery || propriete || !canCreate ? undefined : 'Ajouter un camion'}
          onAction={openCreate}
        />
      ) : vue === 'tableau' ? (
        <div className="rounded-lg border border-border bg-card" data-testid="trucks-table">
          <Table>
            <TableHeader>
              <TableRow>
                {COLONNES_TABLEAU.map((colonne) => {
                  const actif = tri.cle === colonne.cle;
                  return (
                    <TableHead
                      key={colonne.cle || colonne.titre}
                      className={colonne.droite ? 'text-right' : undefined}
                      aria-sort={
                        actif ? (tri.sens === 'asc' ? 'ascending' : 'descending') : undefined
                      }
                    >
                      {colonne.cle ? (
                        <button
                          type="button"
                          onClick={() => trierPar(colonne.cle)}
                          className="inline-flex items-center gap-1 uppercase hover:text-foreground"
                          data-testid={`trucks-sort-${colonne.cle}`}
                        >
                          {colonne.titre}
                          {actif ? (
                            tri.sens === 'asc' ? (
                              <ArrowUp className="h-3 w-3" aria-hidden="true" />
                            ) : (
                              <ArrowDown className="h-3 w-3" aria-hidden="true" />
                            )
                          ) : null}
                        </button>
                      ) : (
                        <span className="sr-only">{colonne.titre}</span>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedTrucks.map((truck, index) => {
                const status = effectiveTruckStatus(truck);
                const gps = getGpsBadge(truck);
                return (
                  <TableRow
                    key={truck.id}
                    className={cn('cursor-pointer', index % 2 === 1 && 'bg-muted/30')}
                    onClick={() => openTruck(truck)}
                    data-testid={`truck-row-${truck.id}`}
                  >
                    <TableCell className="whitespace-nowrap">
                      <span className="matricule-plate text-sm">{truck.matricule}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {truck.tank_number || '—'}
                    </TableCell>
                    <TableCell data-testid={`truck-ownership-${truck.id}`}>
                      <Badge
                        className={cn(
                          'status-badge text-xs',
                          isRented(truck)
                            ? 'bg-info/15 text-info border-info/30'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {libelle('truckOwnership', truck.ownership_type || 'PROPRE')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          'status-badge text-xs',
                          status === 'FREE'
                            ? 'bg-success/15 text-success'
                            : 'bg-flame/15 text-flame-ink',
                        )}
                      >
                        {libelle('truckStatus', status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {truck.driver_full_name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {formatVolume(truck.capacity_liters)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn('status-badge text-xs', getTechnicalBadge(truck.technical_status))}
                      >
                        {libelle('technicalStatus', truck.technical_status, 'En bon état')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('status-badge text-xs border', gps.className)}>
                        <Radio className="mr-1 h-3 w-3" />
                        {gps.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{renderActions(truck)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : vue === 'statut' ? (
        <div
          className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5"
          data-testid="trucks-by-status"
        >
          {COLONNES_STATUT.map((colonne) => {
            const camions = pagedTrucks.filter((truck) =>
              colonne.statuts.includes(effectiveTruckStatus(truck)),
            );
            return (
              <section
                key={colonne.id}
                className="min-w-0 rounded-lg border border-border bg-muted/30 p-2"
                aria-label={colonne.titre}
                data-testid={`trucks-column-${colonne.id}`}
              >
                <h2 className="mb-2 flex items-center justify-between px-1 text-sm font-semibold">
                  {colonne.titre}
                  <span className="text-xs font-medium text-muted-foreground">{camions.length}</span>
                </h2>
                <div className="space-y-2">
                  {camions.length === 0 ? (
                    <p className="px-1 py-3 text-xs text-muted-foreground">Aucun camion</p>
                  ) : (
                    camions.map((truck) => (
                      <Card
                        key={truck.id}
                        className="card-base card-interactive"
                        onClick={() => openTruck(truck)}
                        data-testid={`truck-card-${truck.id}`}
                      >
                        <CardContent className="space-y-1.5 p-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <span className="matricule-plate text-sm">{truck.matricule}</span>
                              {truck.tank_number ? (
                                <span className="truncate text-xs font-bold text-muted-foreground">
                                  {truck.tank_number}
                                </span>
                              ) : null}
                            </div>
                            {renderActions(truck)}
                          </div>
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span>{formatVolume(truck.capacity_liters)}</span>
                            <Badge
                              className={cn(
                                'status-badge text-[10px]',
                                isRented(truck)
                                  ? 'bg-info/15 text-info border-info/30'
                                  : 'bg-muted text-muted-foreground',
                              )}
                            >
                              {libelle('truckOwnership', truck.ownership_type || 'PROPRE')}
                            </Badge>
                          </div>
                          {truck.driver_full_name ? (
                            <p className="truncate text-xs text-muted-foreground">{truck.driver_full_name}</p>
                          ) : null}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="go-md2-lg3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {pagedTrucks.map((truck) => {
            const status = effectiveTruckStatus(truck);
            return (
            <Card
              key={truck.id}
              className="card-base card-interactive relative overflow-hidden"
              onClick={() => openTruck(truck)}
              data-testid={`truck-card-${truck.id}`}
            >
              {/* Status corner indicator */}
              <div
                className={cn(
                  'truck-status-indicator',
                  status === 'FREE' && 'free',
                  status === 'ON_TRIP' && 'on-trip',
                  status === 'MAINTENANCE' && 'maintenance',
                )}
              />

              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <div className="matricule-plate text-sm">
                        {truck.matricule}
                      </div>
                      {truck.tank_number ? (
                        <span className="text-xs font-bold text-muted-foreground truncate">
                          {truck.tank_number}
                        </span>
                      ) : null}
                      {truck.is_shared || (truck.shared_org_count ?? 0) > 1 ? (
                        <Badge
                          className="bg-flame/15 text-flame-ink border border-flame/30 text-[10px] px-1.5 py-0.5"
                          title={`Camion partagé avec ${truck.shared_org_count || 1} organisation(s)`}
                        >
                          Partagé · {truck.shared_org_count || 1}
                        </Badge>
                      ) : null}
                    </div>
                    {truck.company_id &&
                    companiesById.get(truck.company_id)?.name ? (
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {nomLisible(companiesById.get(truck.company_id).name)}
                      </p>
                    ) : truck.company_name ? (
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {nomLisible(truck.company_name)}
                      </p>
                    ) : null}
                  </div>
                  {renderActions(truck)}
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Capacité</span>
                    <span className="font-medium">
                      {formatVolume(truck.capacity_liters)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Statut</span>
                    <Badge
                      className={cn(
                        'status-badge text-xs',
                        status === 'FREE'
                          ? 'bg-success/15 text-success'
                          : status === 'ON_TRIP'
                            ? 'bg-flame/15 text-flame-ink'
                            : 'bg-flame/15 text-flame-ink',
                      )}
                    >
                      {libelle('truckStatus', status)}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-testid={`truck-ownership-${truck.id}`}>
                    <span className="text-sm text-muted-foreground">Propriété</span>
                    <Badge
                      className={cn(
                        'status-badge text-xs',
                        truck.ownership_type === 'LOUE'
                          ? 'bg-info/15 text-info border-info/30'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {libelle('truckOwnership', truck.ownership_type || 'PROPRE')}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      État technique
                    </span>
                    <Badge
                      className={cn(
                        'status-badge text-xs',
                        getTechnicalBadge(truck.technical_status),
                      )}
                    >
                      {libelle('technicalStatus', truck.technical_status, 'En bon état')}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{LEXIQUE.suiviGps}</span>
                    <Badge className={cn('status-badge text-xs border', getGpsBadge(truck).className)}>
                      <Radio className="mr-1 h-3 w-3" />
                      {getGpsBadge(truck).label}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {pagedTrucks.length > 0 ? <BarrePagination pagination={pagination} nom="camions" /> : null}

      <TruckFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        truck={truckToEdit}
        onSaved={loadTrucks}
      />

      {/* Delete confirm */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {truckToDelete?.is_shared || (truckToDelete?.shared_org_count ?? 0) > 1
                ? 'Retirer ce camion de votre flotte ?'
                : 'Supprimer ce camion ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {truckToDelete?.is_shared || (truckToDelete?.shared_org_count ?? 0) > 1
                ? "Le camion reste disponible pour les autres organisations qui l'utilisent. Vos trajets liés à ce camion seront également supprimés de votre flotte."
                : 'La suppression est définitive : les trajets et l’historique de ce camion seront aussi supprimés.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setTruckToDelete(null);
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : truckToDelete?.is_shared || (truckToDelete?.shared_org_count ?? 0) > 1 ? (
                'Retirer'
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrucksPage;
